import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  PlatformAutomationDraft,
  PLATFORM_LOCALES,
  TIMEZONE_OPTIONS,
  TRIGGER_OPTIONS,
} from "../../../cores/platformAutomations/types";

type Props = {
  draft: PlatformAutomationDraft;
  onChange: (draft: PlatformAutomationDraft) => void;
};

export default function PlatformAutomationSettingsPanel({
  draft,
  onChange,
}: Props) {
  const freeTrialEnabled =
    draft.conditions?.all?.some(
      (rule) =>
        rule.field === "freeTrial" && rule.operator === "eq" && rule.value === true
    ) ?? false;

  const stopConverted =
    draft.stopRules?.some((r) => r.event === "converted") ?? false;
  const stopUnsubscribed =
    draft.stopRules?.some((r) => r.event === "unsubscribed") ?? false;
  const isArchivedLoginTrigger =
    (draft.trigger || "user_email_verified") === "archived_user_login_attempt";

  const update = (patch: Partial<PlatformAutomationDraft>) =>
    onChange({ ...draft, ...patch });

  const toggleFreeTrial = (enabled: boolean) => {
    const otherRules =
      draft.conditions?.all?.filter((r) => r.field !== "freeTrial") || [];
    update({
      conditions: {
        all: enabled
          ? [...otherRules, { field: "freeTrial", operator: "eq", value: true }]
          : otherRules,
      },
    });
  };

  const toggleStopRule = (
    event: string,
    enabled: boolean,
    extra: Record<string, unknown> = {}
  ) => {
    const without = (draft.stopRules || []).filter((r) => r.event !== event);
    update({
      stopRules: enabled
        ? [...without, { event, ...extra }]
        : without,
    });
  };

  return (
    <div className="pa-settings">
      <h5 className="pa-settings__title">Configuración del flujo</h5>
      <div className="pa-settings__grid">
        <label className="pa-field">
          <span>Disparador</span>
          <select
            value={draft.trigger || "user_email_verified"}
            onChange={(e) => update({ trigger: e.target.value })}
          >
            {TRIGGER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>

        <label className="pa-field">
          <span>Idioma por defecto</span>
          <select
            value={draft.defaultLocale || "es"}
            onChange={(e) => update({ defaultLocale: e.target.value })}
          >
            {PLATFORM_LOCALES.map((locale) => (
              <option key={locale} value={locale}>
                {locale}
              </option>
            ))}
          </select>
        </label>

        <label className="pa-field">
          <span>Zona horaria</span>
          <select
            value={draft.defaultTimezone || "America/Santiago"}
            onChange={(e) => update({ defaultTimezone: e.target.value })}
          >
            {TIMEZONE_OPTIONS.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
        </label>

        <label className="pa-field">
          <span>Inscripción</span>
          <select
            value={draft.enrollmentPolicy || "once_per_user"}
            onChange={(e) => update({ enrollmentPolicy: e.target.value })}
          >
            <option value="once_per_user">Una vez por usuario</option>
          </select>
        </label>
      </div>

      {isArchivedLoginTrigger && (
        <div className="pa-settings__grid pa-settings__grid--full">
          <label className="pa-field pa-field--full">
            <span>Emails de aviso interno (separados por coma)</span>
            <input
              type="text"
              value={draft.internalNotifyEmails || "rami@wembii.com"}
              onChange={(e) => update({ internalNotifyEmails: e.target.value })}
              placeholder="rami@wembii.com, ventas@wembii.com"
            />
          </label>
        </div>
      )}

      <div className="pa-settings__checks">
        {!isArchivedLoginTrigger && (
        <label className="pa-check">
          <input
            type="checkbox"
            checked={freeTrialEnabled}
            onChange={(e) => toggleFreeTrial(e.target.checked)}
          />
          <span>Solo usuarios en free trial</span>
        </label>
        )}
        <label className="pa-check">
          <input
            type="checkbox"
            checked={stopConverted}
            onChange={(e) =>
              toggleStopRule("converted", e.target.checked, {
                stopScope: "automation",
              })
            }
          />
          <span>Detener si el usuario convierte a plan pago</span>
        </label>
        <label className="pa-check">
          <input
            type="checkbox"
            checked={stopUnsubscribed}
            onChange={(e) =>
              toggleStopRule("unsubscribed", e.target.checked, {
                stopScope: "channel",
                channel: "email",
              })
            }
          />
          <span>Detener si se da de baja del canal email</span>
        </label>
      </div>

      <div className="pa-settings__hint">
        <FontAwesomeIcon icon={["fas", "circle-info"]} />
        <span>
          El JSON se genera automáticamente al guardar. No necesitás editarlo
          manualmente.
        </span>
      </div>
    </div>
  );
}
